import { StageBadge } from "@components/campaigns/stages/StageBadge";
import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { Loader2, Sparkles, Users } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router";
import {
  CHANNELS,
  type ChannelSpec,
  contactSummary,
  type DraftKind,
  draftFor,
  draftState,
  draftStateLabel,
  latestContact,
  type WorkspaceCandidate,
} from "./outreachDrafts";

export type ListScope = "outreach" | "all";

interface OutreachDraftListProps {
  campaignId: string;
  candidates: WorkspaceCandidate[];
  outreachCount: number;
  totalCount: number;
  scope: ListScope;
  onScopeChange: (scope: ListScope) => void;
  /** The candidate open in the editor. */
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Candidates ticked for a bulk draft, inbox-style. */
  checkedIds: Set<string>;
  onToggleChecked: (id: string) => void;
  onToggleAll: () => void;
  generatingCount: number;
  canDraft: boolean;
  cannotDraftReason: string | null;
  onDraft: (kind: DraftKind) => void;
  notice: string | null;
}

const STATE_CHIP: Record<ReturnType<typeof draftState>, string> = {
  none: "bg-text-muted/10 text-text-faint",
  generating: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  draft: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  edited: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  error: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

function ScopeChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
        active
          ? "bg-accent text-white"
          : "bg-card text-text-muted ring-1 ring-ring hover:text-text-secondary"
      }`}
    >
      {label}
    </button>
  );
}

/** Left pane of the Outreach workspace: the candidates and their per-channel
 *  draft status, like a Drafts folder (#3169). */
export function OutreachDraftList({
  campaignId,
  candidates,
  outreachCount,
  totalCount,
  scope,
  onScopeChange,
  selectedId,
  onSelect,
  checkedIds,
  onToggleChecked,
  onToggleAll,
  generatingCount,
  canDraft,
  cannotDraftReason,
  onDraft,
  notice,
}: OutreachDraftListProps) {
  const pipelineHref = `/campaigns/${campaignId}/run/pipeline/longlist`;
  const checkedListed = candidates.filter((cc) => checkedIds.has(cc.id)).length;
  const allChecked = candidates.length > 0 && checkedListed === candidates.length;
  const someChecked = checkedListed > 0 && !allChecked;
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someChecked;
  }, [someChecked]);
  const draftDisabledReason = !canDraft
    ? cannotDraftReason
    : checkedListed === 0
      ? "Select candidates first"
      : null;

  return (
    <div className="flex h-full flex-col rounded-lg bg-card shadow-sm ring-1 ring-ring">
      <div className="space-y-2 border-b border-border p-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <ScopeChip
            active={scope === "outreach"}
            label={`At outreach stage (${outreachCount})`}
            onClick={() => onScopeChange("outreach")}
          />
          <ScopeChip
            active={scope === "all"}
            label={`Whole pipeline (${totalCount})`}
            onClick={() => onScopeChange("all")}
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <label className="mr-1 flex items-center gap-1.5 text-xs text-text-muted">
            <input
              ref={selectAllRef}
              type="checkbox"
              aria-label="Select all candidates"
              checked={allChecked}
              disabled={candidates.length === 0}
              onChange={onToggleAll}
              className="h-3.5 w-3.5 rounded border-input-border text-accent focus:ring-accent"
            />
            {checkedListed > 0 ? `${checkedListed} selected` : "Select all"}
          </label>
          {CHANNELS.map((spec: ChannelSpec) => (
            <button
              key={spec.kind}
              type="button"
              disabled={draftDisabledReason !== null}
              title={draftDisabledReason ?? `Draft ${spec.pluralLabel} for the selected candidates`}
              onClick={() => onDraft(spec.kind)}
              className="flex items-center gap-1 rounded bg-accent/10 px-2 py-1 text-xs font-medium text-accent hover:bg-accent/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Sparkles size={11} /> Draft {spec.pluralLabel}
            </button>
          ))}
          {generatingCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-text-muted">
              <Loader2 size={12} className="animate-spin" /> Drafting {generatingCount}…
            </span>
          )}
        </div>
        {notice && (
          <p role="status" className="text-xs text-text-muted">
            {notice}
          </p>
        )}
      </div>

      {candidates.length === 0 ? (
        <div className="p-6 text-center text-xs text-text-muted">
          <Users size={20} className="mx-auto mb-2 text-text-faint" />
          {scope === "outreach" ? (
            <>
              <p>No candidates at the outreach stage yet.</p>
              <p className="mt-1">
                <Link to={pipelineHref} className="text-accent hover:underline">
                  Move candidates from the longlist
                </Link>
                , or switch to the whole pipeline.
              </p>
            </>
          ) : (
            <p>No candidates in this campaign yet.</p>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-y-auto" aria-label="Candidates">
          {candidates.map((cc) => {
            const selected = cc.id === selectedId;
            const contact = latestContact(cc);
            const subtitle = [cc.candidate.currentRoleTitle, cc.candidate.currentCompany]
              .filter(Boolean)
              .join(" at ");
            return (
              <li key={cc.id} className="flex items-start gap-2 px-3 py-2">
                <input
                  type="checkbox"
                  aria-label={`Select ${cc.candidate.fullName}`}
                  checked={checkedIds.has(cc.id)}
                  onChange={() => onToggleChecked(cc.id)}
                  className="mt-1 h-3.5 w-3.5 rounded border-input-border text-accent focus:ring-accent"
                />
                <button
                  type="button"
                  onClick={() => onSelect(cc.id)}
                  aria-current={selected ? "true" : undefined}
                  data-testid={`draft-row-${cc.id}`}
                  className={`flex min-w-0 flex-1 flex-col items-start gap-1 rounded px-2 py-1 text-left hover:bg-card-hover ${
                    selected ? "bg-accent/5 ring-1 ring-inset ring-accent/40" : ""
                  }`}
                >
                  <div className="flex w-full items-center gap-2">
                    <span className="truncate text-sm font-medium text-text-primary">
                      {cc.candidate.fullName}
                    </span>
                    {typeof cc.candidate.referenceNumber === "number" && (
                      <CandidateReferenceBadge referenceNumber={cc.candidate.referenceNumber} />
                    )}
                    {cc.stage !== "outreach" && (
                      <span className="ml-auto">
                        <StageBadge stage={cc.stage} />
                      </span>
                    )}
                  </div>
                  {subtitle && <span className="text-xs text-text-muted">{subtitle}</span>}
                  <div className="flex flex-wrap items-center gap-1">
                    {CHANNELS.map((spec) => {
                      const draft = draftFor(cc, spec);
                      return (
                        <span
                          key={spec.kind}
                          className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${STATE_CHIP[draftState(draft)]}`}
                        >
                          {spec.shortLabel}: {draftStateLabel(draft)}
                        </span>
                      );
                    })}
                  </div>
                  {contact && (
                    <span className="text-[10px] text-text-faint">{contactSummary(contact)}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
